import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { getUsersGraph } from "../../../../api";
import { DashboardGraphViewType } from "../../../../constants/types";
import {
  getWeeklyChartLabels,
  getYearWeekMap,
  MONTH_NAMES_FULL,
} from "../../../../helpers/chartDateUtils";
import { useStickyYAxis } from "../../../../hooks/useStickyYAxis";
import { getCSS } from "../../../assets/ts/_utils";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";
import { ChartDateFilter } from "./ChartDateFilter";

type Props = {
  className?: string;
  labels?: string[];
  dataList?: number[];
  title?: string;
  eyebrow?: string;
  description?: string;
};

const UsersPerMonthChart: React.FC<Props> = ({
  className = "",
  labels: initialLabels,
  dataList: initialDataList,
  title: initialTitle = "Users Per Month",
  eyebrow = "Growth Pulse",
  description: initialDescription,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<ApexCharts | null>(null);
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const prevScrollableRef = useRef<boolean>(false);
  const { mode } = useThemeMode();

  const [viewType, setViewType] = useState<DashboardGraphViewType>(
    DashboardGraphViewType.Monthly,
  );
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);

  const [chartData, setChartData] = useState<{
    labels: string[];
    dataList: number[];
  }>({
    labels: initialLabels || [],
    dataList: initialDataList || [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutateAsync: getUsersGraphAPI } = useMutation(getUsersGraph);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload: { Year: number; ViewType: number; Month?: number } = {
        Year: year,
        ViewType: viewType,
      };
      if (viewType === DashboardGraphViewType.Daily) {
        payload.Month = month;
      }
      const res = await getUsersGraphAPI(payload);
      const fetchedData =
        res?.result?.usersPerMonth?.data || res?.result?.data;
      const fetchedLabels = fetchedData?.labels || [];
      const fetchedDataList = fetchedData?.datasets?.[0]?.data || [];
      setChartData({
        labels: fetchedLabels,
        dataList: fetchedDataList,
      });
    } catch (error) {
      setChartData({ labels: [], dataList: [] });
    } finally {
      setIsLoading(false);
    }
  }, [getUsersGraphAPI, year, month, viewType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset horizontal scroll on filter change
  useEffect(() => {
    if (scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollLeft = 0;
    }
  }, [viewType, month, year]);

  const { labels, dataList } = chartData;
  const hasData = dataList.some((value) => value > 0);
  const isScrollable = labels.length > 14;

  // Dynamic title based on view type
  const displayTitle =
    initialTitle === "Users Per Month"
      ? viewType === DashboardGraphViewType.Daily
        ? "Users Per Day"
        : viewType === DashboardGraphViewType.Weekly
        ? "Users Per Week"
        : "Users Per Month"
      : initialTitle;

  const displayDescription =
    initialDescription ||
    (viewType === DashboardGraphViewType.Daily
      ? "Daily sign-up momentum for the selected month."
      : viewType === DashboardGraphViewType.Weekly
      ? "Weekly sign-up momentum across the year."
      : "Monthly sign-up momentum with a cleaner view of spikes and quieter periods.");

  const chartLabels = React.useMemo(() => {
    if (viewType === DashboardGraphViewType.Weekly) {
      return getWeeklyChartLabels(labels, year);
    }
    return labels;
  }, [labels, viewType, year]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    if (!labels.length || !dataList.length || !hasData) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      chartRef.current.innerHTML = "";
      return;
    }

    const height = parseInt(getCSS(chartRef.current, "height")) || 350;
    const options = getBarChartOptions(
      height,
      chartLabels,
      dataList,
      isScrollable,
      viewType,
      year,
      month,
    );

    // Rebuild chart only on first render or when scrollability changes (structural change)
    const scrollabilityChanged = prevScrollableRef.current !== isScrollable;
    prevScrollableRef.current = isScrollable;

    if (!chartInstanceRef.current || scrollabilityChanged) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      const chart = new ApexCharts(chartRef.current, options);
      chart.render();
      chartInstanceRef.current = chart;
    } else {
      // Update data without full re-animation
      chartInstanceRef.current.updateOptions(options, true, true);
    }
  }, [
    chartRef,
    mode,
    chartLabels,
    labels,
    dataList,
    hasData,
    isScrollable,
    viewType,
    year,
    month,
  ]);

  // Clean up chart instance on unmount
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  useStickyYAxis(scrollWrapperRef, chartRef, isScrollable);

  const itemWidth = viewType === DashboardGraphViewType.Weekly ? 74 : 52;

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div className="card-header border-0 pb-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{displayTitle}</h3>
          <p className="dashboard-chart-card__description">
            {displayDescription}
          </p>
        </div>

        <ChartDateFilter
          viewType={viewType}
          onViewTypeChange={setViewType}
          year={year}
          onYearChange={setYear}
          month={month}
          onMonthChange={setMonth}
        />
      </div>

      <div className="card-body dashboard-chart-card__body">
        {!hasData && !isLoading && (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">UP</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No user registrations yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                New registrations will start filling this trend view.
              </span>
            </span>
          </div>
        )}

        {isScrollable && (
          <div className="dashboard-chart-scroll-hint-container">
            <div className="dashboard-chart-scroll-hint">
              <span className="dashboard-chart-scroll-hint__icon">↔</span>
              <span>
                Scroll horizontally to explore all {labels.length}{" "}
                {viewType === DashboardGraphViewType.Daily
                  ? "days"
                  : viewType === DashboardGraphViewType.Weekly
                  ? "weeks"
                  : "items"}
              </span>
            </div>
          </div>
        )}

        <div
          ref={scrollWrapperRef}
          className="dashboard-chart-scroll-wrapper"
        >
          <div
            ref={chartRef}
            className="dashboard-chart-canvas"
            style={{
              height: "350px",
              minWidth: isScrollable ? `${labels.length * itemWidth}px` : undefined,
              width: isScrollable ? `${labels.length * itemWidth}px` : "100%",
              opacity: isLoading ? 0.4 : hasData ? 1 : 0.22,
              transition: "opacity 0.2s ease",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export { UsersPerMonthChart };

function getBarChartOptions(
  height: number,
  labels: string[],
  dataList: number[],
  isScrollable: boolean,
  viewType: DashboardGraphViewType,
  year: number,
  month: number,
): ApexOptions {
  const maxVal = Math.max(...dataList, 1);
  const isWeekly = viewType === DashboardGraphViewType.Weekly;
  const isDaily = viewType === DashboardGraphViewType.Daily;
  const isMonthly = viewType === DashboardGraphViewType.Monthly;
  const weekMap = isWeekly
    ? getYearWeekMap(year, Math.max(labels.length, 53))
    : null;

  return {
    series: [{ name: "Users", data: dataList }],
    chart: {
      fontFamily: "inherit",
      type: "bar",
      height,
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 50 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    colors: ["#0F766E"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.15,
        gradientToColors: ["#38BDF8"],
        inverseColors: false,
        opacityFrom: 0.96,
        opacityTo: 0.82,
        stops: [0, 100],
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: isWeekly ? "38%" : isScrollable ? "48%" : "44%",
        borderRadius: 8,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val > 0 ? val.toString() : "";
      },
      offsetY: -14,
      style: {
        fontSize: "11px",
        fontWeight: 800,
        colors: ["#0F172A"],
      },
      background: {
        enabled: false,
        dropShadow: {
          enabled: false,
        },
      },
      dropShadow: {
        enabled: false,
      },
    },
    legend: { show: false },
    stroke: { show: true, width: 0, colors: ["transparent"] },
    xaxis: {
      categories: labels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickPlacement: "between",
      labels: {
        rotate: isScrollable ? -45 : 0,
        rotateAlways: isScrollable,
        hideOverlappingLabels: false,
        trim: false,
        style: {
          colors: "#64748B",
          fontSize: "11px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      min: 0,
      max: maxVal <= 5 ? 5 : undefined,
      forceNiceScale: true,
      labels: {
        style: { colors: "#94A3B8", fontSize: "12px", fontWeight: 500 },
        formatter: function (val: number) {
          return Math.round(val).toString();
        },
      },
    },
    grid: {
      borderColor: "#E2E8F0",
      strokeDashArray: 5,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 18, right: 12, bottom: 0, left: 12 },
    },
    tooltip: {
      enabled: true,
      theme: "light",
      style: { fontSize: "13px" },
      x: {
        show: true,
        formatter: function (val: any, opts?: any) {
          const index = opts?.dataPointIndex ?? 0;
          if (isWeekly && weekMap) {
            const raw = labels[index] || val;
            const match = String(raw).match(/\d+/);
            const weekNum = match ? parseInt(match[0], 10) : index + 1;
            const info = weekMap.get(weekNum);
            if (info) {
              return `${info.axisLabel} • ${info.dateRange}`;
            }
          }
          if (isDaily) {
            const day = labels[index] || index + 1;
            const monthName = MONTH_NAMES_FULL[month - 1] || "";
            return `${monthName} ${day}, ${year}`;
          }
          if (isMonthly) {
            const monthName = MONTH_NAMES_FULL[index] || val;
            return `${monthName} ${year}`;
          }
          return String(val);
        },
      },
      y: {
        formatter: function (val: number) {
          return val + " user(s)";
        },
      },
      marker: {
        show: false,
      },
    },
    states: {
      hover: {
        filter: { type: "lighten", value: 0.04 },
      },
    },
  };
}
